import DOMPurify, { DOMPurify as DOMPurifyInstance } from "dompurify";
import { PAGE_FILE_SRC_ATTRIBUTE, isPageFileUrl } from "../files/PageFileUrl";

import { ConfigurationProvider } from "../configuration/ConfigurationProvider";

/** Azure DevOps embeds, already allowed by the host's CSP `frame-src`. */
const AZURE_DEVOPS_ORIGIN = "https://dev.azure.com";

/**
 * Origins a page may frame: the Atlas origins of this environment (`Atlas.Origins` in `appconfiguration.{env}.json`, the same
 * key the host puts in the CSP `frame-src`) and Azure DevOps.
 */
export function allowedFrameOrigins(): string[] {
    let atlas: string[] = [];
    try {
        atlas = ConfigurationProvider.getConfiguration()?.Atlas?.Origins ?? [];
    } catch {
        atlas = [];
    }
    return [...atlas, AZURE_DEVOPS_ORIGIN]
        .map((origin) => {
            try {
                return new URL(origin).origin;
            } catch {
                return null;
            }
        })
        .filter((origin): origin is string => !!origin && origin.startsWith("https://"));
}

function parse(url: string): URL | null {
    try {
        return new URL(url, window.location.href);
    } catch {
        return null;
    }
}

function isAllowedFrame(src: string | null, origins: string[]): boolean {
    if (!src) return false;
    const parsed = parse(src);
    return !!parsed && parsed.protocol === "https:" && origins.includes(parsed.origin);
}

/** Image sources a page may use: https, inline `data:image/…`, or a path on Ark's own origin. */
function isAllowedImageSrc(src: string): boolean {
    const value = src.trim();
    if (/^data:image\//i.test(value)) return true;
    if (!/^[a-z][a-z0-9+.-]*:/i.test(value) && !value.startsWith("//")) return true; // relative
    return parse(value)?.protocol === "https:";
}

let purifier: DOMPurifyInstance | null = null;
let frameOrigins: string[] = [];

/** One DOMPurify instance with Ark's hooks, so other users of DOMPurify are not affected. */
function getPurifier(): DOMPurifyInstance {
    if (purifier) return purifier;
    const instance = DOMPurify(window);

    instance.addHook("afterSanitizeAttributes", (node) => {
        const el = node as Element;
        if (!el.tagName) return;
        const tag = el.tagName.toUpperCase();

        // data-ark-src is turned into src by PageFileResolver: only an attachment image may carry it.
        if (el.hasAttribute(PAGE_FILE_SRC_ATTRIBUTE) && !(tag === "IMG" && isPageFileUrl(el.getAttribute(PAGE_FILE_SRC_ATTRIBUTE)))) {
            el.removeAttribute(PAGE_FILE_SRC_ATTRIBUTE);
        }

        if (tag === "IMG" && el.hasAttribute("src") && !isAllowedImageSrc(el.getAttribute("src") ?? "")) {
            el.removeAttribute("src");
        }

        if (tag === "IFRAME") {
            if (!isAllowedFrame(el.getAttribute("src"), frameOrigins)) el.removeAttribute("src");
            el.removeAttribute("srcdoc");
        }

        if (el.hasAttribute("target")) {
            if (el.getAttribute("target") === "_blank") el.setAttribute("rel", "noopener noreferrer");
            else el.removeAttribute("target");
        }
    });

    instance.addHook("uponSanitizeElement", (node, data) => {
        // An iframe that is not an allowed embed goes away entirely instead of staying as an empty frame.
        if (data.tagName === "iframe" && !isAllowedFrame((node as Element).getAttribute?.("src") ?? null, frameOrigins)) {
            node.parentNode?.removeChild(node);
        }
    });

    purifier = instance;
    return instance;
}

/**
 * Sanitizes the HTML that markdown-it produces for a page body (markdown-it runs with `html: true`, so any editor controls the
 * markup every reader gets). Keeps standard markdown HTML, images (https, `data:image`, Ark attachments through `data-ark-src`),
 * and iframes to the Atlas origins and Azure DevOps; drops scripts, event handlers, forms, `<style>`, other iframes, and
 * `javascript:`/`data:` URLs outside images.
 */
export function sanitizePageHtml(html: string): string {
    if (!html) return html;
    frameOrigins = allowedFrameOrigins();
    return getPurifier().sanitize(html, {
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["target", "allowfullscreen", "frameborder", "scrolling", "loading", PAGE_FILE_SRC_ATTRIBUTE],
        FORBID_TAGS: ["form", "style", "link", "meta", "base", "object", "embed"],
        FORBID_ATTR: ["srcdoc", "formaction", "action"],
        ALLOW_UNKNOWN_PROTOCOLS: false,
    }) as string;
}
